import React, { ReactElement, useState, CSSProperties } from 'react';
import { getDateWithTime } from '../../../utils/dateTimeUtils';
import { FollowUpTimeLineProps } from '../../interfaces/tpaInterfaces';
import { ReactComponent as FollowUpChevronUp } from '../../../assets/images/svg/FollowUpChevronDown.svg';
import { ReactComponent as FollowUpChevronDown } from '../../../assets/images/svg/FollowUpChevronUp.svg';
import { ReactComponent as IhxSuportUpdate } from '../../../assets/images/svg/IhxSuportUpdate.svg';
import { ReactComponent as IhxSystemUpdate } from '../../../assets/images/svg/IhxSystemUpdate.svg';
import { ReactComponent as EmailFollowUpIcon } from '../../../../src/assets/images/svg/EmailFollowUpIcon.svg';
import { ReactComponent as IhxSupportUpdateSubIcon } from '../../../../src/assets/images/svg/IhxSupportUpdateSubIcon.svg';
import { ReactComponent as FollowUpChevronBlackDown } from '../../../../src/assets/images/svg/FollowUpChevronBlackDown.svg';
import { ReactComponent as FollowUpChevronBlackUp } from '../../../../src/assets/images/svg/FollowUpChevronBlackUp.svg';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { isEmpty } from 'lodash';
import { formatNumberWithLeadingZero } from '../../../utils/utility';

const FollowUpDetails: React.FC<FollowUpTimeLineProps> = (
  props: FollowUpTimeLineProps,
): ReactElement => {
  const { content, onClickGroup, expandedGroupIds, isExpanded } = props;
  const { timelineColorCode } = useSelector(
    (state: RootState): any => state.admissionDetails,
  );
  const getFollowUpMeta = (id: number): any => {
    const colorData = timelineColorCode?.[id];
    const bgColor = colorData?.bgColor;
    const tagColor = colorData?.cardColor;
    switch (id) {
      case 1:
        return {
          label: 'IHX System Update',
          bgColor: bgColor || '#EDECFEF4',
          tagColor: tagColor || '#DADAFB',
          icon: <IhxSystemUpdate />,
        };
      case 2:
        return {
          label: 'IHX Support Team Update',
          bgColor: bgColor || '#EDFCF4',
          tagColor: tagColor || '#B1F1CF',
          icon: <IhxSuportUpdate />,
          subIcon: <IhxSupportUpdateSubIcon />,
        };
      case 3:
        return {
          label: 'Email Reminder via Hospital',
          bgColor: bgColor || '#FAF8E4',
          tagColor: tagColor || '#F6F1BC',
          icon: <EmailFollowUpIcon />,
        };
    }
  };

  const isGroupExpanded = expandedGroupIds.has(content.groupId);
  const subGroupKeys = Object?.keys(content)?.filter((key: string): any =>
    key.startsWith('subGroup_'),
  );

  const [expandedTitleIds, setExpandedTitleIds] = useState<Set<string>>(
    new Set(),
  );

  const toggleExpand = (titleId: string): void => {
    const updated = new Set(expandedTitleIds);
    if (expandedTitleIds.has(titleId)) {
      updated.delete(titleId);
    } else {
      updated.add(titleId);
    }
    setExpandedTitleIds(updated);
  };
  const firstSubgroup = subGroupKeys?.[0];

  if (!isExpanded) {
    return (
      <div className="follow-up-container-unexpanded">
        <span className="ihx-updates">IHX Updates</span>
        <div className="count-toggle">
          {!isEmpty(subGroupKeys) && (
            <span
              className="click-toggle"
              onClick={(): void => onClickGroup(content.groupId)}
            >
              {formatNumberWithLeadingZero(subGroupKeys.length)}
              {isExpanded ? (
                <FollowUpChevronUp />
              ) : (
                <span className="mx-1">
                  <FollowUpChevronDown />
                </span>
              )}
            </span>
          )}
        </div>
        <span className="timestamp">
          {getDateWithTime(content?.[firstSubgroup]?.[0]?.date || '-')}
          {/*to get the date time of latest status */}
        </span>
      </div>
    );
  }

  if (!isGroupExpanded) {
    return <></>;
  }

  // Combine all remarks grouped by title
  const allSubItems = subGroupKeys?.flatMap(
    (key: string): any => content[key] || [],
  );
  const groupedBySubGroupId: Record<string, any[]> = {};
  allSubItems?.map((item: any): any => {
    const subGroupKey = item?.subGroupId;
    if (!groupedBySubGroupId[subGroupKey]) {
      groupedBySubGroupId[subGroupKey] = [];
    }
    groupedBySubGroupId[subGroupKey].push(item);
  });
  return (
    <div
      className={`expanded-followup-container follow-up-expand-wrapper ${
        isExpanded && 'expanded'
      }`}
    >
      <span className="ihx-updates ihx-updates-expanded">IHX Updates</span>
      <span
        className="ihx-updates-expanded expanded-group"
        onClick={(): void => onClickGroup(content.groupId)}
      >
        <FollowUpChevronUp />
      </span>
      {Object?.entries(groupedBySubGroupId)?.map(
        ([subGroupId, items]: any): ReactElement => {
          const followUpId = items[0]?.followUpcardTitleId;
          const { label, bgColor, tagColor, icon, subIcon } =
            getFollowUpMeta(followUpId);
          const firstIndex = items[0]; // to show only the latest update
          const restIndexes = items?.slice(1); // to show other updates post expanded
          const firstIndexRemarks = firstIndex?.remarks;
          const isOpen = expandedTitleIds.has(subGroupId);
          return (
            <div
              key={followUpId}
              className="follow-up-card"
              style={{ '--card-bg-color': bgColor } as CSSProperties}
            >
              <div className="follow-up-header">
                {icon}
                <span
                  className="follow-up-tag"
                  style={{ '--tag-bg-color': tagColor } as CSSProperties}
                >
                  {firstIndex.followUpcardTitle || label}
                </span>
              </div>
              {!isEmpty(restIndexes) && (
                <span
                  className="follow-up-toggle"
                  style={
                    { '--toggle-bg-color': tagColor } as CSSProperties
                  }
                  onClick={(): void => toggleExpand(subGroupId)}
                >
                  {isOpen ? (
                    <FollowUpChevronBlackUp />
                  ) : (
                    <>
                      + {restIndexes?.length}
                      <FollowUpChevronBlackDown className="mx-1" />
                    </>
                  )}
                </span>
              )}
              {firstIndexRemarks && (
                <div className="follow-up-remark px-4">
                  {subIcon && <span>{subIcon}</span>}
                  <span>{firstIndexRemarks}</span>
                </div>
              )}
              <div className="timestamp">
                {getDateWithTime(firstIndex?.date)}
              </div>

              <div
                className={`follow-up-expand-wrapper ${isOpen && 'expanded'}`}
              >
                {restIndexes?.map(
                  (item: any, index: number): ReactElement => (
                    <div key={index} className="mt-20">
                      {item?.remarks && (
                        <div className="follow-up-remark px-4">
                          {subIcon && <span>{subIcon}</span>}
                          <span>{item?.remarks}</span>
                        </div>
                      )}
                      <div className="timestamp">
                        {getDateWithTime(item?.date)}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          );
        },
      )}
    </div>
  );
};

export default FollowUpDetails;
